import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  SelectChangeEvent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  ToggleButtonGroup,
  ToggleButton,
  FormHelperText,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Delete,
  // Person,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { bookingsService } from './bookings.service';
import { servicesService } from '../services/services.service';
import { Service } from '../services/services.dto';
import { diagnosticCentersService } from './diagnostic-centers.service';
import { DiagnosticCenter } from './diagnostic-centers.dto';
import { slotsService } from '../slots/slots.service';
import { formatTime } from '../../helpers';

// import { Booking } from './bookings.dto';
import {
  BOOKING_STATUSES,
  BOOKING_STATUS,
  DEFAULT_CURRENCY,
  PAYMENT_METHOD,
  PAYMENT_STATUS,
} from '@/constants';
import { formatDate } from '../../helpers';

import { useToast } from '../../contexts/ToastContext';
import {
  isValidPhoneFormat,
  sanitizePhoneForSubmit,
} from '../../utils/phoneValidation';
import {
  extractApiErrorMessage,
  mapApiErrorToField,
} from '../../utils/apiErrorMapping';
const paymentMethodOptions = [
  { value: 4, label: 'Prepaid' },
  { value: 2, label: 'Bank Transfer' },
  { value: 99, label: 'Other' },
  { value: 1, label: 'Stripe' },
];

const EditBooking = () => {
  const { showSuccess, showError, showWarning } = useToast();
  const navigate = useNavigate();
  // Every "return to the list" action below (cancel, back arrow, and the
  // redirect after a successful save/delete) goes back in history rather
  // than pushing a fresh navigate('/bookings'). Bookings.tsx keeps its
  // date-filter/search/page state in the URL query string (see that file),
  // so a bare '/bookings' push landed on the unfiltered list, discarding
  // whatever filter was applied before the admin opened this booking to
  // edit it. Falls back to a plain '/bookings' navigation only when there's
  // no in-app history to go back to (e.g. a direct link/bookmark straight
  // to this edit page).
  const goBackToList = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/bookings');
    }
  };
  const { id } = useParams();
  const { token, logout } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    healthCard: '',
    versionCode: '',
    dob: '',
    gender: '',
    streetAddress: '',
    city: '',
    postalCode: '',
    email: '',
    phone: '',
    homeNumber: '',
    workNumber: '',
    diagnosticCenterId: '',
    diagnosticCenterName: '',
    diagnosticCenterPhone: '',
    diagnosticCenterEmail: '',
    diagnosticCenterAddress: '',
    diagnosticCenterCity: '',
    diagnosticCenterPostalCode: '',
    status: 0,
    paymentStatus: 1,
    paymentMethod: 4,
    paymentReference: '',
    reportStatus: 0 as number | null,
    bookingDate: '',
    bookingTime: '',
    medicalInformation: '',
    serviceId: 0,
    amount: 0,
    bookingRef: '',
    createdAt: '',
    // Health Background
    height: '',
    heightUnit: 'cm',
    weight: '',
    weightUnit: 'kg',
    knownMedicalConditions: '',
    currentMedications: '',
    allergies: '',
    // Risk Factors
    highBloodPressure: 'no',
    highCholesterol: 'no',
    diabetes: 'no',
    smoker: 'never',
    familyHistoryHeartDisease: 'no',
    previousCancer: 'no',
    familyHistoryCancer: 'no',
    occupationalExposures: 'no',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [diagnosticCenters, setDiagnosticCenters] = useState<
    DiagnosticCenter[]
  >([]);
  const [availableSlots, setAvailableSlots] = useState<
    { id: number; startTime: string; status: number; isBookable?: boolean }[]
  >([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  // MUI's DatePicker only opens on the calendar icon by default - clicking
  // anywhere else in the field just focuses the text input. Controlling
  // `open` and adding an onClick to the text field makes the whole field
  // clickable, matching how every other Select/dropdown on this form
  // already behaves.
  const [bookingDatePickerOpen, setBookingDatePickerOpen] = useState(false);
  const [dobPickerOpen, setDobPickerOpen] = useState(false);
  const [originalBooking, setOriginalBooking] = useState<{
    bookingDate: string;
    bookingTime: string;
  } | null>(null);

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
  ];

  // Set up logout callback and fetch data
  useEffect(() => {
    // Set up logout callbacks
    bookingsService.setLogoutCallback(logout);
    servicesService.setLogoutCallback(logout);
    diagnosticCentersService.setLogoutCallback(logout);
    slotsService.setLogoutCallback(logout);

    const fetchData = async () => {
      if (!token || !id) return;

      try {
        setLoading(true);

        // Fetch booking data, services, and diagnostic centers in parallel
        const [booking, servicesData, centersData] = await Promise.all([
          bookingsService.getBooking(token, parseInt(id)),
          servicesService.getServices(token, { limit: 100, status: 1 }),
          diagnosticCentersService.getDiagnosticCenters(token, {
            limit: 100,
            status: 1,
          }),
        ]);

        // console.log('Fetched booking data:', booking);
        // console.log('Fetched services:', servicesData.data);
        // console.log('Fetched diagnostic centers:', centersData.data);

        setFormData(prev => ({
          ...prev,
          firstName: booking.firstName,
          lastName: booking.lastName,
          healthCard: booking.healthCard || '',
          versionCode: booking.versionCode || '',
          dob: booking.dob,
          gender: booking.gender || '',
          streetAddress: booking.streetAddress || '',
          city: booking.city || '',
          postalCode: booking.postalCode || '',
          email: booking.email,
          phone: booking.phone,
          homeNumber: booking.homeNumber || '',
          workNumber: booking.workNumber || '',
          diagnosticCenterId: booking.diagnosticCenterId || '',
          diagnosticCenterName: booking.diagnosticCenterName || '',
          diagnosticCenterPhone: booking.diagnosticCenterPhone || '',
          diagnosticCenterEmail: booking.diagnosticCenterEmail || '',
          diagnosticCenterAddress: booking.diagnosticCenterAddress || '',
          diagnosticCenterCity: booking.diagnosticCenterCity || '',
          diagnosticCenterPostalCode: booking.diagnosticCenterPostalCode || '',
          status: booking.status,
          paymentStatus: booking.paymentStatus ?? 1,
          paymentMethod: booking.paymentMethod ?? 4,
          paymentReference: booking.paymentReference || '',
          reportStatus: booking.reportStatus || 0,
          bookingDate: booking.bookingDate,
          bookingTime: booking.bookingTime,
          medicalInformation: booking.medicalInformation || '',
          serviceId: booking.serviceId,
          amount: Number(booking.amount),
          bookingRef: booking.bookingRef,
          createdAt: booking.createdAt,
        }));

        setOriginalBooking({
          bookingDate: booking.bookingDate,
          bookingTime: booking.bookingTime,
        });

        // Prefill questionnaire fields
        if (booking.questionnaires && booking.questionnaires.length > 0) {
          setFormData(prev => {
            const updated = { ...prev };

            booking.questionnaires?.forEach(q => {
              if (q.question === 'Height') {
                const parts = q.answer.split(' ');
                updated.height = parts[0] || '';
                updated.heightUnit = parts[1] || 'cm';
              } else if (q.question === 'Weight') {
                const parts = q.answer.split(' ');
                updated.weight = parts[0] || '';
                updated.weightUnit = parts[1] || 'kg';
              } else {
                const mapping: Record<string, string> = {
                  'Known medical conditions': 'knownMedicalConditions',
                  'Current medications': 'currentMedications',
                  Allergies: 'allergies',
                  'High blood pressure': 'highBloodPressure',
                  'High cholesterol': 'highCholesterol',
                  Diabetes: 'diabetes',
                  Smoker: 'smoker',
                  'Family history of heart disease (1 or more first-degree relatives with early signs of CAD; men <55 and women <65 years of age)':
                    'familyHistoryHeartDisease',
                  'Previous cancer': 'previousCancer',
                  'Family history of cancer': 'familyHistoryCancer',
                  'Occupational exposures (e.g., asbestos, smoke, chemicals)':
                    'occupationalExposures',
                };

                // Risk-factor fields are radio-button enums
                // ('yes'/'no', or 'never'/'current'/'former' for Smoker) -
                // the RadioGroup below only matches an exact, lowercase
                // value, so an answer saved with different casing (e.g.
                // an older booking, or one submitted through a path that
                // stored the option's Label instead of its value) left
                // every risk-factor radio showing neither Yes nor No
                // selected, even though an answer was saved. Free-text
                // fields (medical conditions, medications, allergies) are
                // left as-is - lowercasing those would mangle real text.
                const riskFactorFields = new Set([
                  'highBloodPressure',
                  'highCholesterol',
                  'diabetes',
                  'smoker',
                  'familyHistoryHeartDisease',
                  'previousCancer',
                  'familyHistoryCancer',
                  'occupationalExposures',
                ]);

                const field = mapping[q.question];
                if (field) {
                  (updated as any)[field] = riskFactorFields.has(field)
                    ? q.answer.trim().toLowerCase()
                    : q.answer;
                }
              }
            });

            return updated;
          });
        }

        setServices(servicesData.data);
        setDiagnosticCenters(centersData.data);
      } catch (err) {
        if (err instanceof Error) {
          showError(err.message);
        } else {
          showError('Failed to fetch booking data');
        }
        goBackToList();
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, id, navigate, logout]);

  // Fetch available slots when bookingDate or serviceId changes
  useEffect(() => {
    const fetchAvailableSlots = async () => {
      // Only fetch if both date and serviceId are provided
      if (
        !formData.bookingDate ||
        !formData.serviceId ||
        formData.serviceId === 0
      ) {
        setAvailableSlots([]);
        return;
      }

      try {
        setLoadingSlots(true);
        const slots = await slotsService.getAvailableSlots(
          formData.bookingDate,
          formData.serviceId,
          1,
          id ? Number(id) : undefined
        );

        const updatedSlots = Array.isArray(slots) ? [...slots] : [];

        // Add original booking slot only if we're still viewing its original
        // date and it does not already exist in the freshly-fetched list
        if (
          originalBooking?.bookingTime &&
          originalBooking.bookingDate === formData.bookingDate &&
          !updatedSlots.some(
            slot => slot.startTime === originalBooking.bookingTime
          )
        ) {
          updatedSlots.push({
            id: -1,
            startTime: originalBooking.bookingTime,
            status: 1,
            isBookable: true,
          });
        }

        setAvailableSlots(updatedSlots);

        // If current bookingTime is no longer bookable, clear it
        if (
          formData.bookingTime &&
          !updatedSlots.some(
            s => s.startTime === formData.bookingTime && s.isBookable
          )
        ) {
          setFormData(prev => ({ ...prev, bookingTime: '' }));
        }
      } catch (err) {
        console.error('Error fetching available slots:', err);
        setAvailableSlots([]);
        // Optionally show error message
        if (err instanceof Error) {
          showWarning(`Failed to load available slots: ${err.message}`);
        }
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchAvailableSlots();
    // formData.bookingTime is intentionally excluded: this effect sets it, so
    // including it would create a feedback loop (see AddBooking's equivalent effect).
  }, [
    formData.bookingDate,
    formData.serviceId,
    originalBooking?.bookingTime,
    id,
  ]);

  useEffect(() => {
    // Don't touch the slot while the booking data or slots are still loading
    if (loading || loadingSlots) return;

    // All slots that can be selected (includes the injected original slot)
    const visibleSlots = availableSlots.filter(s => s.isBookable);

    const current = formData.bookingTime;
    const originalTime = originalBooking?.bookingTime;

    // 1. If the current selected time exists and is still in the visible list, keep it (allows user changes!)
    if (current && visibleSlots.some(s => s.startTime === current)) {
      return;
    }

    // 2. If no valid slot is selected, try to default to the original booking time if available
    if (originalTime && visibleSlots.some(s => s.startTime === originalTime)) {
      setFormData(prev => ({ ...prev, bookingTime: originalTime }));
      return;
    }

    // 3. Otherwise, clear it and let the admin pick one themselves,
    // rather than silently defaulting to whichever slot happens to be
    // first in the list.
    setFormData(prev => ({
      ...prev,
      bookingTime: '',
    }));
  }, [
    availableSlots,
    loadingSlots,
    loading,
    formData.bookingTime,
    originalBooking?.bookingTime,
  ]);

  const handleChange =
    (field: keyof typeof formData) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent
    ) => {
      if (errors[field as string]) {
        setErrors(prev => ({ ...prev, [field as string]: '' }));
      }

      let rawValue = (event.target as HTMLInputElement).value;

      // Special handling for phone fields - only allow + and numbers
      if (
        field === 'phone' ||
        field === 'homeNumber' ||
        field === 'workNumber'
      ) {
        rawValue = rawValue.replace(/[^+0-9]/g, '');
        // Ensure + can only be at the start
        if (rawValue.includes('+')) {
          const plusCount = (rawValue.match(/\+/g) || []).length;
          if (plusCount > 1 || rawValue.indexOf('+') !== 0) {
            rawValue = rawValue.replace(/\+/g, '');
            if ((event.target as HTMLInputElement).value.startsWith('+')) {
              rawValue = '+' + rawValue;
            }
          }
        }
      }

      // If service is selected, auto-fill the amount
      if (field === 'serviceId') {
        const selectedService = services.find(s => s.id === Number(rawValue));
        if (selectedService) {
          setFormData({
            ...formData,
            serviceId: Number(rawValue),
            amount: Number(selectedService.price || 0),
          });
          if (errors.amount) {
            setErrors(prev => ({ ...prev, amount: '' }));
          }
        } else {
          setFormData({ ...formData, [field]: Number(rawValue) });
        }
      } else if (field === 'diagnosticCenterId') {
        // If diagnostic center is selected, auto-fill center details
        const selectedCenter = diagnosticCenters.find(
          c => c.id === Number(rawValue)
        );
        if (selectedCenter) {
          setFormData({
            ...formData,
            diagnosticCenterId: String(selectedCenter.id),
            diagnosticCenterName: selectedCenter.name || '',
            diagnosticCenterAddress: selectedCenter.address || '',
            diagnosticCenterCity: selectedCenter.city || '',
            diagnosticCenterPostalCode: selectedCenter.postalCode || '',
            diagnosticCenterEmail: selectedCenter.email || '',
            diagnosticCenterPhone: selectedCenter.phone || '',
          });
        } else {
          setFormData({ ...formData, [field]: rawValue });
        }
      } else if (field === 'status' || field === 'paymentMethod') {
        setFormData({ ...formData, [field]: Number(rawValue) });
      } else {
        setFormData({ ...formData, [field]: rawValue as any });
      }
    };

  const handleDateChange =
    (field: keyof typeof formData) => (value: Moment | null) => {
      const rawValue =
        value && value.isValid() ? value.format('YYYY-MM-DD') : '';
      setFormData(prev => ({ ...prev, [field]: rawValue as any }));

      if (errors[field as string]) {
        setErrors(prev => ({ ...prev, [field as string]: '' }));
      }
    };

  const handleToggleChange =
    (field: 'heightUnit' | 'weightUnit') =>
    (_event: React.MouseEvent<HTMLElement>, newUnit: string | null) => {
      if (newUnit !== null) {
        setFormData(prev => ({ ...prev, [field]: newUnit }));
      }
    };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      // Remove + and spaces for length check
      const cleanPhone = sanitizePhoneForSubmit(formData.phone);
      if (!isValidPhoneFormat(formData.phone)) {
        newErrors.phone = 'Phone number can only contain + and numbers';
      } else if (cleanPhone.length !== 10) {
        newErrors.phone = 'Phone number must be exactly 10 digits';
      }
    }

    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    // Status 7 = "Awaiting Scheduling & Payment" (admin sent a scheduling
    // link, patient hasn't picked a slot yet) - there's genuinely no
    // date/time to have here, so don't block editing everything else
    // (e.g. patient details) on filling in a slot the patient hasn't
    // chosen. Matches AddBooking.tsx's equivalent skip for
    // sendSchedulingLink.
    if (formData.status !== 7) {
      if (!formData.bookingDate) {
        newErrors.bookingDate = 'Booking date is required';
      }

      if (!formData.bookingTime) {
        newErrors.bookingTime = 'Booking time is required';
      }
    }

    if (!formData.serviceId || formData.serviceId === 0) {
      newErrors.serviceId = 'Service is required';
    }

    if (!formData.diagnosticCenterId) {
      newErrors.diagnosticCenterId = 'Diagnostic center is required';
    }

    if (
      !formData.amount ||
      parseFloat(formData.amount.toString() as unknown as string) < 0
    ) {
      newErrors.amount = 'Valid amount is required';
    }

    // Canadian postal code validation
    if (formData.postalCode.trim()) {
      const postalCodeRegex = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
      if (!postalCodeRegex.test(formData.postalCode.trim())) {
        newErrors.postalCode =
          'Invalid postal code format. Use A1A 1A1 or A1A1A1';
      }
    }

    // Health card validation (optional, but if provided must match Ontario format: 10 digits + 2 letters)
    if (formData.healthCard.trim()) {
      // Normalize: remove spaces and convert to uppercase
      const normalizedHealthCard = formData.healthCard
        .replace(/\s/g, '')
        .toUpperCase();
      // Ontario format: 10 digits followed by 2 uppercase letters (e.g., 1234567890AB)
      if (!/^\d{10}[A-Z]{2}$/.test(normalizedHealthCard)) {
        newErrors.healthCard =
          'Ontario health card must be 10 digits + 2 letters (e.g., 1234567890AB)';
      }
    }

    // Version code validation (optional, but if provided must be 2 letters)
    if (formData.versionCode.trim()) {
      if (!/^[A-Za-z]{2}$/.test(formData.versionCode.trim())) {
        newErrors.versionCode = 'Version code must be exactly 2 letters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showError(
        '⬆️ Please scroll up and fix all validation errors before submitting'
      );
      // Smooth scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!token || !id) return;

    setSaving(true);
    try {
      const bookingData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        healthCard: formData.healthCard || '',
        versionCode: formData.versionCode || '',
        dob: formData.dob,
        gender: formData.gender,
        streetAddress: formData.streetAddress || '',
        city: formData.city || '',
        postalCode: formData.postalCode || '',
        email: formData.email,
        phone: formData.phone,
        homeNumber: formData.homeNumber || '',
        workNumber: formData.workNumber || '',
        diagnosticCenterId: formData.diagnosticCenterId || undefined,
        diagnosticCenterName: formData.diagnosticCenterName || '',
        diagnosticCenterPhone: formData.diagnosticCenterPhone || '',
        diagnosticCenterEmail: formData.diagnosticCenterEmail || '',
        diagnosticCenterAddress: formData.diagnosticCenterAddress || '',
        diagnosticCenterCity: formData.diagnosticCenterCity || '',
        diagnosticCenterPostalCode: formData.diagnosticCenterPostalCode || '',
        status: formData.status,
        paymentStatus: formData.paymentStatus,
        paymentMethod: formData.paymentMethod,
        paymentReference: formData.paymentReference || '',
        // Sent whenever the admin has set them - including while Awaiting
        // Scheduling, so a date/time saved by the admin actually persists.
        // Omitted (not sent as empty strings) when empty, since the
        // backend's @IsDateString() validation rejects ''.
        ...(formData.bookingDate && formData.bookingTime
          ? {
              bookingDate: formData.bookingDate,
              bookingTime: formData.bookingTime,
            }
          : {}),
        medicalInformation: formData.medicalInformation || '',
        serviceId: formData.serviceId,
        amount: Number(formData.amount),
        medicalAnswers: [
          {
            question: 'Height',
            answer: formData.height,
            unit: formData.heightUnit,
          },
          {
            question: 'Weight',
            answer: formData.weight,
            unit: formData.weightUnit,
          },
          {
            question: 'Known medical conditions',
            answer: formData.knownMedicalConditions || 'None',
          },
          {
            question: 'Current medications',
            answer: formData.currentMedications || 'None',
          },
          { question: 'Allergies', answer: formData.allergies || 'None' },
          {
            question: 'High blood pressure',
            answer: formData.highBloodPressure,
          },
          { question: 'High cholesterol', answer: formData.highCholesterol },
          { question: 'Diabetes', answer: formData.diabetes },
          { question: 'Smoker', answer: formData.smoker },
          {
            question:
              'Family history of heart disease (1 or more first-degree relatives with early signs of CAD; men <55 and women <65 years of age)',
            answer: formData.familyHistoryHeartDisease,
          },
          { question: 'Previous cancer', answer: formData.previousCancer },
          {
            question: 'Family history of cancer',
            answer: formData.familyHistoryCancer,
          },
          {
            question:
              'Occupational exposures (e.g., asbestos, smoke, chemicals)',
            answer: formData.occupationalExposures,
          },
        ].filter(a => a.answer !== ''),
      };

      await bookingsService.updateBooking(
        token,
        parseInt(id),
        bookingData as any
      );

      showSuccess('Booking updated successfully!');

      setTimeout(() => {
        goBackToList();
      }, 1500);
    } catch (err) {
      const message = extractApiErrorMessage(err) || 'Failed to update booking';
      const { field } = mapApiErrorToField(message, Object.keys(formData));
      if (field) {
        setErrors(prev => ({ ...prev, [field]: message }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    goBackToList();
  };

  const handleDelete = async () => {
    if (!token || !id) return;

    try {
      setDeleting(true);
      await bookingsService.deleteBooking(token, parseInt(id));
      showSuccess('Booking deleted successfully!');

      setTimeout(() => {
        goBackToList();
      }, 1500);
    } catch (err) {
      if (err instanceof Error) {
        showError(err.message);
      } else {
        showError('Failed to delete booking');
      }
    } finally {
      setDeleting(false);
    }
    setDeleteDialog(false);
  };

  const getStatus = (statusKey: number) => {
    const statusResult = BOOKING_STATUS(statusKey);
    return statusResult;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading booking data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={goBackToList} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Edit color="primary" sx={{ mr: 1 }} />
              <Typography variant="h4" component="h1">
                Edit Booking
              </Typography>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {formData.bookingRef}
              </Typography>
              <Chip
                label={getStatus(formData.status).label}
                color={getStatus(formData.status).color as any}
                variant="outlined"
              />
            </Box>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Update booking information and appointment details
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Booking Settings Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Booking Settings
                </Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={String(formData.status)}
                    label="Status"
                    onChange={handleChange('status')}
                  >
                    {BOOKING_STATUSES.map(status => (
                      <MenuItem key={status.key} value={String(status.key)}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Payment Option</InputLabel>
                  <Select
                    value={String(formData.paymentMethod)}
                    label="Payment Option"
                    onChange={handleChange('paymentMethod')}
                  >
                    {paymentMethodOptions.map(option => (
                      <MenuItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Payment Reference"
                  value={formData.paymentReference}
                  onChange={handleChange('paymentReference')}
                  margin="normal"
                  placeholder="Receipt number, note, or external reference"
                  helperText="Admin-managed bookings are treated as externally paid."
                />

                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!errors.serviceId}
                >
                  <InputLabel>Service *</InputLabel>
                  <Select
                    value={
                      formData.serviceId === 0 ? '' : String(formData.serviceId)
                    }
                    label="Service *"
                    onChange={handleChange('serviceId')}
                    onOpen={() => {
                      if (errors.serviceId) {
                        setErrors(prev => ({ ...prev, serviceId: '' }));
                      }
                    }}
                  >
                    {services.map(service => (
                      <MenuItem key={service.id} value={service.id}>
                        {service.title} - ${service.price}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.serviceId && (
                    <FormHelperText>{errors.serviceId}</FormHelperText>
                  )}
                </FormControl>

                <FormControl
                  fullWidth
                  margin="normal"
                  required
                  error={!!errors.diagnosticCenterId}
                >
                  <InputLabel>Diagnostic Center</InputLabel>
                  <Select
                    value={formData.diagnosticCenterId}
                    label="Diagnostic Center"
                    onChange={handleChange('diagnosticCenterId')}
                    onOpen={() => {
                      if (errors.diagnosticCenterId) {
                        setErrors(prev => ({
                          ...prev,
                          diagnosticCenterId: '',
                        }));
                      }
                    }}
                  >
                    {diagnosticCenters.map(center => (
                      <MenuItem key={center.id} value={String(center.id)}>
                        {center.name} - {center.address} - {center.city}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.diagnosticCenterId && (
                    <FormHelperText>{errors.diagnosticCenterId}</FormHelperText>
                  )}
                </FormControl>

                {/* Display selected service image */}
                {formData.serviceId !== 0 &&
                  (() => {
                    const selectedService = services.find(
                      s => s.id === formData.serviceId
                    );
                    const imageUrl =
                      selectedService?.image_main || selectedService?.image;

                    if (imageUrl) {
                      return (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Service Image
                          </Typography>
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={selectedService?.title || 'Service'}
                            sx={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: 200,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                        </Box>
                      );
                    }
                    return null;
                  })()}

                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange('amount')}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ mr: 1 }}>{DEFAULT_CURRENCY}</Typography>
                    ),
                  }}
                />

                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Current payment setup:{' '}
                    {PAYMENT_STATUS(formData.paymentStatus).label} via{' '}
                    {PAYMENT_METHOD(formData.paymentMethod).label}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Created on
                </Typography>
                <Typography variant="h6">
                  {formatDate(formData.createdAt)}
                </Typography>
              </CardContent>
            </Card>

            {/* Service Information Card */}
            {formData.serviceId !== 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Service Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {(() => {
                    const selectedService = services.find(
                      s => s.id === formData.serviceId
                    );
                    if (selectedService) {
                      return (
                        <>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Service Title
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {selectedService.title}
                            </Typography>
                          </Box>

                          {selectedService.description && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Description
                              </Typography>
                              <Typography variant="body2">
                                {selectedService.description}
                              </Typography>
                            </Box>
                          )}

                          {selectedService.duration && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Duration
                              </Typography>
                              <Typography variant="body1">
                                {selectedService.duration} minutes
                              </Typography>
                            </Box>
                          )}

                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Price
                            </Typography>
                            <Typography variant="h6" color="primary">
                              {DEFAULT_CURRENCY}
                              {selectedService.price}
                            </Typography>
                          </Box>
                        </>
                      );
                    }
                    return null;
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Diagnostic Center Card */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Diagnostic Center
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {formData.diagnosticCenterAddress && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1">
                      {formData.diagnosticCenterAddress}
                    </Typography>
                  </Box>
                )}

                {formData.diagnosticCenterCity && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      City
                    </Typography>
                    <Typography variant="body1">
                      {formData.diagnosticCenterCity}
                    </Typography>
                  </Box>
                )}

                {formData.diagnosticCenterPostalCode && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Postal Code
                    </Typography>
                    <Typography variant="body1">
                      {formData.diagnosticCenterPostalCode}
                    </Typography>
                  </Box>
                )}

                {formData.diagnosticCenterEmail && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {formData.diagnosticCenterEmail}
                    </Typography>
                  </Box>
                )}

                {formData.diagnosticCenterPhone && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">
                      {formData.diagnosticCenterPhone}
                    </Typography>
                  </Box>
                )}

                {!formData.diagnosticCenterId &&
                  !formData.diagnosticCenterAddress &&
                  !formData.diagnosticCenterCity &&
                  !formData.diagnosticCenterPostalCode &&
                  !formData.diagnosticCenterEmail &&
                  !formData.diagnosticCenterPhone && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic' }}
                    >
                      No diagnostic center information available
                    </Typography>
                  )}
              </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card
              sx={{ mt: 2, border: '1px solid', borderColor: 'error.light' }}
            >
              <CardContent>
                <Typography variant="h6" color="error" gutterBottom>
                  Danger Zone
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Once you delete a booking, there is no going back. Please be
                  certain.
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteDialog(true)}
                  disabled={deleting}
                  fullWidth
                >
                  Delete Booking
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={3}>
                    {/* Patient Information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Patient Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={formData.firstName}
                        onChange={handleChange('firstName')}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={formData.lastName}
                        onChange={handleChange('lastName')}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        error={!!errors.email}
                        helperText={errors.email}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={formData.phone}
                        onChange={handleChange('phone')}
                        error={!!errors.phone}
                        helperText={
                          errors.phone || 'Format: +14251234567 (10 digits)'
                        }
                        placeholder="+14251234567"
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Date of Birth"
                        value={
                          formData.dob
                            ? // dob arrives as a full UTC ISO timestamp for a
                              // date-only value - moment(formData.dob) would
                              // convert to the browser's local timezone and
                              // display a day earlier. Re-parsing the UTC
                              // calendar date as a bare local date string
                              // avoids that shift.
                              moment(
                                moment.utc(formData.dob).format('YYYY-MM-DD')
                              )
                            : null
                        }
                        onChange={handleDateChange('dob')}
                        maxDate={moment()}
                        open={dobPickerOpen}
                        onOpen={() => setDobPickerOpen(true)}
                        onClose={() => setDobPickerOpen(false)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            error: !!errors.dob,
                            helperText: errors.dob,
                            onClick: () => setDobPickerOpen(true),
                            // Without this, the underlying <input> stays
                            // editable, so a click just places a text
                            // cursor instead of reliably opening the
                            // picker - readOnly is what actually routes
                            // every click through onClick above.
                            inputProps: {
                              readOnly: true,
                              style: { cursor: 'pointer' },
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required error={!!errors.gender}>
                        <InputLabel>Gender</InputLabel>
                        <Select
                          value={formData.gender}
                          label="Gender"
                          onChange={handleChange('gender')}
                        >
                          {genderOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.gender && (
                          <FormHelperText>{errors.gender}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Health Card"
                        value={formData.healthCard}
                        onChange={handleChange('healthCard')}
                        error={!!errors.healthCard}
                        helperText={
                          errors.healthCard ||
                          'Ontario format: 10 digits + 2 letters (optional)'
                        }
                        placeholder="1234567890AB"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Version Code"
                        value={formData.versionCode}
                        onChange={handleChange('versionCode')}
                        error={!!errors.versionCode}
                        helperText={
                          errors.versionCode || '2 letters (optional)'
                        }
                        placeholder="XA"
                      />
                    </Grid>

                    {/* Appointment Details */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Appointment Details
                      </Typography>
                    </Grid>

                    {formData.status === 7 && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Date and time will be picked by the patient via the
                          scheduling link emailed to them.
                        </Alert>
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Booking Date"
                        value={
                          formData.bookingDate
                            ? // Same UTC-timestamp-for-a-date-only-value shift
                              // as dob above.
                              moment(
                                moment
                                  .utc(formData.bookingDate)
                                  .format('YYYY-MM-DD')
                              )
                            : null
                        }
                        onChange={handleDateChange('bookingDate')}
                        minDate={moment()}
                        open={bookingDatePickerOpen}
                        onOpen={() => setBookingDatePickerOpen(true)}
                        onClose={() => setBookingDatePickerOpen(false)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            error: !!errors.bookingDate,
                            helperText: errors.bookingDate,
                            onClick: () => setBookingDatePickerOpen(true),
                            // Without this, the underlying <input> stays
                            // editable, so a click just places a text
                            // cursor instead of reliably opening the
                            // picker - readOnly is what actually routes
                            // every click through onClick above.
                            inputProps: {
                              readOnly: true,
                              style: { cursor: 'pointer' },
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        margin="normal"
                        error={!!errors.bookingTime}
                        style={{ marginTop: 0 }}
                      >
                        <InputLabel>Booking Time *</InputLabel>

                        <Select
                          value={formData.bookingTime || ''}
                          label="Booking Time *"
                          onChange={handleChange('bookingTime')}
                          // Only disabled while there's genuinely nothing to
                          // pick from yet (still loading, or no date/service
                          // selected). Previously this also disabled the
                          // field whenever the result was zero slots - which
                          // hid the "No slots available" message inside a
                          // dropdown that could then never be opened, making
                          // the field look broken/unresponsive instead of
                          // explaining why. Leaving it enabled lets the
                          // admin open it and see that message (and the
                          // FormHelperText below surfaces the same reason
                          // without needing to open it at all).
                          disabled={
                            loadingSlots ||
                            !formData.bookingDate ||
                            !formData.serviceId
                          }
                        >
                          {/* Loading */}
                          {loadingSlots && (
                            <MenuItem disabled>
                              <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={16} />
                                Loading available slots…
                              </Box>
                            </MenuItem>
                          )}

                          {/* No date/service selected */}
                          {!loadingSlots &&
                            (!formData.bookingDate || !formData.serviceId) && (
                              <MenuItem disabled>
                                Select date and service first
                              </MenuItem>
                            )}

                          {/* No slot records at all for this date/service.
                              !!formData.serviceId (not the bare number) -
                              serviceId 0 means "no service picked yet", and
                              `0 && x` short-circuits to the number 0, which
                              React renders as a literal "0" on the page
                              instead of rendering nothing. */}
                          {!loadingSlots &&
                            formData.bookingDate &&
                            !!formData.serviceId &&
                            availableSlots.length === 0 && (
                              <MenuItem disabled>
                                No slots available for this date
                              </MenuItem>
                            )}

                          {/* Every slot for the date, including ones that
                              can't be booked (disabled/fully booked) -
                              shown greyed-out and labeled rather than
                              silently omitted, so the dropdown never looks
                              empty/broken when the API actually returned
                              data. */}
                          {availableSlots.map(slot => (
                            <MenuItem
                              key={slot.id}
                              value={slot.startTime}
                              disabled={!slot.isBookable}
                            >
                              {formatTime(slot.startTime)}
                              {/* slot.status is 0 (admin-disabled) or 1
                                  (enabled) only - "fully booked" is never
                                  written into it (see booking.service.ts's
                                  refreshSlotStatus), so a not-bookable
                                  slot with status 1 is booked, not
                                  disabled. Checking `!== 1` here used to
                                  treat any non-1 value as "Disabled",
                                  which mislabeled a genuinely fully-booked
                                  slot the same as an admin-disabled one. */}
                              {!slot.isBookable &&
                                (slot.status === 0
                                  ? ' (Disabled)'
                                  : ' (Fully booked)')}
                            </MenuItem>
                          ))}
                        </Select>

                        {errors.bookingTime ? (
                          <FormHelperText>{errors.bookingTime}</FormHelperText>
                        ) : !formData.serviceId ? (
                          // The Select above is disabled while no service is
                          // picked, so its "Select date and service first"
                          // MenuItem is unreachable - without this, the field
                          // just sits greyed out with no explanation, and no
                          // network request either, looking broken.
                          <FormHelperText>
                            Select a service above to see available times
                          </FormHelperText>
                        ) : (
                          !loadingSlots &&
                          formData.bookingDate &&
                          availableSlots.length === 0 && (
                            <FormHelperText>
                              No slots available for this date - please pick a
                              different date
                            </FormHelperText>
                          )
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Medical Information"
                        multiline
                        rows={3}
                        value={formData.medicalInformation}
                        onChange={handleChange('medicalInformation')}
                      />
                    </Grid>

                    {/* Health Background Section */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Health Background
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="flex-start">
                        <TextField
                          fullWidth
                          label="Height"
                          type="number"
                          value={formData.height}
                          onChange={handleChange('height')}
                          sx={{ mr: 1 }}
                        />
                        <ToggleButtonGroup
                          value={formData.heightUnit}
                          exclusive
                          onChange={handleToggleChange('heightUnit')}
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          <ToggleButton value="cm">cm</ToggleButton>
                          <ToggleButton value="inches">inches</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="flex-start">
                        <TextField
                          fullWidth
                          label="Weight"
                          type="number"
                          value={formData.weight}
                          onChange={handleChange('weight')}
                          sx={{ mr: 1 }}
                        />
                        <ToggleButtonGroup
                          value={formData.weightUnit}
                          exclusive
                          onChange={handleToggleChange('weightUnit')}
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          <ToggleButton value="kg">kg</ToggleButton>
                          <ToggleButton value="lbs">lbs</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Known medical conditions"
                        value={formData.knownMedicalConditions}
                        onChange={handleChange('knownMedicalConditions')}
                        placeholder="e.g. Hypertension, Asthma..."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Current medications"
                        value={formData.currentMedications}
                        onChange={handleChange('currentMedications')}
                        placeholder="List any medications you are currently taking"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Allergies"
                        value={formData.allergies}
                        onChange={handleChange('allergies')}
                        placeholder="List any allergies"
                      />
                    </Grid>

                    {/* Risk Factors Section */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Risk Factors
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          High blood pressure
                        </FormLabel>
                        <RadioGroup
                          row
                          value={formData.highBloodPressure}
                          onChange={handleChange('highBloodPressure')}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          High cholesterol
                        </FormLabel>
                        <RadioGroup
                          row
                          value={formData.highCholesterol}
                          onChange={handleChange('highCholesterol')}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Diabetes</FormLabel>
                        <RadioGroup
                          row
                          value={formData.diabetes}
                          onChange={handleChange('diabetes')}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Smoker</FormLabel>
                        <RadioGroup
                          row
                          value={formData.smoker}
                          onChange={handleChange('smoker')}
                        >
                          <FormControlLabel
                            value="never"
                            control={<Radio />}
                            label="Never"
                          />
                          <FormControlLabel
                            value="current"
                            control={<Radio />}
                            label="Current"
                          />
                          <FormControlLabel
                            value="former"
                            control={<Radio />}
                            label="Former"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Family history of heart disease (1 or more
                          first-degree relatives with early signs of CAD; men
                          &lt;55 and women &lt;65 years of age)
                        </FormLabel>
                        <RadioGroup
                          row
                          value={formData.familyHistoryHeartDisease}
                          onChange={handleChange('familyHistoryHeartDisease')}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Previous cancer
                        </FormLabel>
                        <RadioGroup
                          row
                          value={formData.previousCancer}
                          onChange={handleChange('previousCancer')}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Family history of cancer
                        </FormLabel>
                        <RadioGroup
                          row
                          value={formData.familyHistoryCancer}
                          onChange={handleChange('familyHistoryCancer')}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Occupational exposures (e.g., asbestos, smoke,
                          chemicals)
                        </FormLabel>
                        <RadioGroup
                          row
                          value={formData.occupationalExposures}
                          onChange={handleChange('occupationalExposures')}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* Address Information */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Address Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Street Address"
                        value={formData.streetAddress}
                        onChange={handleChange('streetAddress')}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={formData.city}
                        onChange={handleChange('city')}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Postal Code"
                        value={formData.postalCode}
                        onChange={handleChange('postalCode')}
                        error={!!errors.postalCode}
                        helperText={errors.postalCode || 'Format: A1A 1A1'}
                        placeholder="M4B 3E2"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      startIcon={<Cancel />}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        saving ? <CircularProgress size={20} /> : <Save />
                      }
                      disabled={saving}
                      sx={{
                        background:
                          'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                        },
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={deleting ? undefined : () => setDeleteDialog(false)}
      >
        <DialogTitle>Delete Booking</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete booking "{formData.bookingRef}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditBooking;
